/* "Seen On You" UGC carousel for the 2026 PDP.

   The track is a native scroll-snap list, so touch scrolling is the browser's
   own and needs no library. This adds the chevrons, the dot pager and video
   playback.

   Playback differs by device on purpose:
     desktop  a popup with the video inline
     mobile   handed to the device's own player -- iOS goes fullscreen through
              webkitEnterFullscreen, others through the Fullscreen API -- which
              is what people expect from a vertical UGC clip, and avoids
              fighting iOS over autoplay inside a custom overlay. */
(function () {
  const MOBILE = () => window.matchMedia('(max-width: 989px)').matches;

  /* ---------- carousel ---------- */
  function pages(track) {
    // Measured from the scroll extent, not from items-per-view. Rounding the
    // ratio of widths reported 4 cards per page in a 565px viewport that only
    // fits 3.7, which came out as a single page and no dots at all even though
    // the track plainly overflowed.
    if (!track.clientWidth) return 1;
    return Math.max(1, Math.ceil(track.scrollWidth / track.clientWidth));
  }

  function currentPage(track) {
    const max = Math.max(1, track.scrollWidth - track.clientWidth);
    const total = pages(track);
    if (total <= 1) return 0;
    return Math.round((track.scrollLeft / max) * (total - 1));
  }

  function syncControls(root) {
    const track = root.querySelector('[data-ugc-track]');
    const prev = root.querySelector('[data-ugc-prev]');
    const next = root.querySelector('[data-ugc-next]');
    const pager = root.querySelector('[data-ugc-pager]');
    if (!track) return;

    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;

    if (!pager) return;
    const total = pages(track);
    if (total <= 1) {
      pager.innerHTML = '';
      return;
    }
    if (pager.children.length !== total) {
      pager.innerHTML = '';
      for (let i = 0; i < total; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pdp2026-ugc__dot';
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.addEventListener('click', () => {
          const max = track.scrollWidth - track.clientWidth;
          track.scrollTo({ left: (max / (total - 1)) * i, behavior: 'smooth' });
        });
        pager.appendChild(dot);
      }
    }
    const active = currentPage(track);
    [...pager.children].forEach((dot, i) => dot.classList.toggle('is-active', i === active));
  }

  function step(root, direction) {
    const track = root.querySelector('[data-ugc-track]');
    if (!track) return;
    track.scrollBy({ left: track.clientWidth * direction, behavior: 'smooth' });
  }

  function initCarousel(root) {
    if (root.dataset.ugcBound) return;
    root.dataset.ugcBound = '1';

    const track = root.querySelector('[data-ugc-track]');
    root.querySelector('[data-ugc-prev]')?.addEventListener('click', () => step(root, -1));
    root.querySelector('[data-ugc-next]')?.addEventListener('click', () => step(root, 1));

    let frame;
    track?.addEventListener('scroll', () => {
      if (frame) return;
      frame = setTimeout(() => {
        frame = null;
        syncControls(root);
      }, 80);
    });

    if (typeof ResizeObserver !== 'undefined' && track) {
      new ResizeObserver(() => syncControls(root)).observe(track);
    }

    // The scroll extent is not settled when init runs, so the first measurement
    // reported a single page and left the pager empty. The ResizeObserver does
    // not catch it either: the track's own box never changes, only its content.
    //
    // Listening for image loads was not enough on its own -- on a warm cache
    // every image is already complete and window.load has already fired, so
    // neither listener ever runs. Re-measure a few times instead, and once more
    // when the carousel first comes into view, which is when a lazy-loaded row
    // finally has real dimensions.
    root.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => syncControls(root), { once: true });
      img.addEventListener('error', () => syncControls(root), { once: true });
    });
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => syncControls(root), { once: true });
    }
    [150, 600, 1500].forEach((delay) => setTimeout(() => syncControls(root), delay));

    if (typeof IntersectionObserver !== 'undefined') {
      const seen = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          syncControls(root);
          seen.disconnect();
        }
      });
      seen.observe(root);
    }

    syncControls(root);
  }

  /* ---------- playback ---------- */
  let modal = null;

  function buildModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'pdp2026-ugc-modal';
    modal.innerHTML =
      '<div class="pdp2026-ugc-modal__inner">' +
      '<button class="pdp2026-ugc-modal__close" type="button" aria-label="Close">' +
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="1.5"/></svg>' +
      '</button>' +
      '<video class="pdp2026-ugc-modal__video" controls playsinline></video>' +
      '</div>';
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.closest('.pdp2026-ugc-modal__close')) {
        closeModal();
      }
    });
    return modal;
  }

  function closeModal() {
    if (!modal) return;
    const video = modal.querySelector('video');
    video.pause();
    video.removeAttribute('src');
    video.load();
    modal.classList.remove('is-open');
    document.body.classList.remove('scroll_lock');
  }

  function playInModal(src) {
    const el = buildModal();
    const video = el.querySelector('video');
    video.src = src;
    el.classList.add('is-open');
    document.body.classList.add('scroll_lock');
    video.play().catch(() => {});
  }

  function playNative(src, card) {
    // Reuse a video element per card so iOS has a real element to take over.
    let video = card.querySelector('video.pdp2026-ugc__native');
    if (!video) {
      video = document.createElement('video');
      video.className = 'pdp2026-ugc__native';
      video.src = src;
      video.controls = true;
      video.setAttribute('preload', 'metadata');
      // No playsinline: iOS then opens its own fullscreen player.
      video.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover';
      card.appendChild(video);
    }
    const toFullscreen =
      video.webkitEnterFullscreen ||
      video.requestFullscreen ||
      video.webkitRequestFullscreen;
    video.play().catch(() => {});
    if (toFullscreen) {
      try {
        toFullscreen.call(video);
      } catch (error) {
        /* stays inline, still playing */
      }
    }
  }

  document.addEventListener('click', (event) => {
    const card = event.target.closest && event.target.closest('[data-ugc-play]');
    if (!card) return;
    const src = card.dataset.video;
    if (!src) return;
    event.preventDefault();
    if (MOBILE()) {
      playNative(src, card);
    } else {
      playInModal(src);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
  });

  function initAll() {
    document.querySelectorAll('[data-pdp2026-ugc]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  window.pdp2026InitSeenOnYou = initAll;
})();
